import { takeLatest, put, call, select } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { Urls } from '../../config/urls';
import { getTask } from '../../services';
import {
  TransacionesActionType,
  getTransactionsSuccess,
  getTransactionsError,
  getPaymentMethodsForTransSuccess,
  getPaymentMethodsForTransError,
  exportTransactionsSuccess,
  exportTransactionsError,
  getTransactionDetailSuccess,
  getTransactionDetailError,
  getPreChargebackFormSuccess,
  getPreChargebackFormError,
  getAffiliationsInfoSuccess,
  getAffiliationsInfoError,
  getTransactionsStatementDataSuccess,
  getTransactionsStatementDataError,
} from './actions';
import { ExportType, Filters, PanelStore } from '../interfaces';
import _ from 'lodash';
import { Refund } from '../refunds/interfaces';
import { TransactionTaxes } from './interfaces';
import { resolveTransactionRefunded } from '../../utils/transactions-utils';

function* fetchRefundsForTransaction(transaction: { invoiceId?: string; externalId?: string }) {
  const state: PanelStore.RootState = yield select();
  const queries = [transaction.invoiceId, transaction.externalId].filter(Boolean);

  for (const query of queries) {
    const { response } = yield call(getTask, {
      url: `${Urls.api.allRefunds}?page=0&transaction_id=${encodeURIComponent(query || '')}`,
      token: state.auth.token,
    });

    if (response?.data?.length) {
      return response.data as Refund[];
    }
  }

  return [] as Refund[];
}

// DLGO-2198: los taxes no deben bloquear el detalle; ante error se muestra sin esa información.
function* fetchTaxesForTransaction(transactionId: number) {
  const state: PanelStore.RootState = yield select();

  const { response } = yield call(getTask, {
    url: Urls.api.transactionTaxes.replace(':id', String(transactionId)),
    token: state.auth.token,
  });

  return (response ?? null) as TransactionTaxes | null;
}

function* getTransactions(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const pageSize = action.rowsPerPage ?? state.transactions.allTransactions?.size ?? 10;

  let params = `?page=${action.page || 0}&page_size=${pageSize}`;

  const filters = action.filter;
  let filterParam = '';
  if (filters) {
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filterParam += filterParam ? '&' : '';
        filterParam += `${_.snakeCase(key)}=${filters[key]}`;
      }
    });
  }

  params += filterParam ? `&${filterParam}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.allTransactions + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getTransactionsSuccess(response));
  } else {
    yield put(getTransactionsError(error));
  }
}

export function* getTransactionsSaga() {
  yield takeLatest(TransacionesActionType.GET_TRANSACTIONS, getTransactions);
}

function* getTransactionsStatementData(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  let params = `?page=${action.page || 0}&size=${action.size || 50}`;

  const filters = action.filter;
  let filterParam = '';
  if (filters) {
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filterParam += filterParam ? '&' : '';
        filterParam += `${_.snakeCase(key)}=${filters[key]}`;
      }
    });
  }

  params += filterParam ? `&${filterParam}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.transactionsStatementData + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getTransactionsStatementDataSuccess(response));
  } else {
    yield put(getTransactionsStatementDataError(error));
  }
}

export function* getTransactionsStatementDataSaga() {
  yield takeLatest(TransacionesActionType.GET_TRANSACTIONS_STATEMENT_DATA, getTransactionsStatementData);
}

function* getTransactionPreChargebackForm(action?: { invoiceId: string }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const merchantId = String(state.dashboard.merchantMe?.id);
  const preChargebackFormUrl = new URL(Urls.api.preChargebackForm);
  preChargebackFormUrl.searchParams.append('invoice_id', action?.invoiceId ?? '');
  preChargebackFormUrl.searchParams.append('merchant_id', merchantId ?? '');

  const { response, error } = yield call(getTask, {
    url: preChargebackFormUrl.toString(),
    token: state.auth.token,
  });

  if (response) {
    yield put(getPreChargebackFormSuccess(response));
  } else {
    yield put(getPreChargebackFormError(error));
  }
}

export function* getPreChargebackFormSaga() {
  yield takeLatest(TransacionesActionType.GET_PRE_CHARGEBACK_FORM, getTransactionPreChargebackForm);
}

function* getTransactionDetail(action?: { invoiceId: string }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.allTransactions}?transaction_id=${action?.invoiceId}`,
    token: state.auth.token,
  });

  if (response && response.data?.length > 0) {
    const transaction = response.data[0];
    const refunds: Refund[] = yield* fetchRefundsForTransaction(transaction);
    const refunded = resolveTransactionRefunded(transaction, refunds);
    const taxes: TransactionTaxes | null = yield* fetchTaxesForTransaction(transaction.id);

    yield put(getTransactionDetailSuccess({ ...transaction, refunded, relatedRefunds: refunds, taxes }));
    return;
  }

  yield put(getTransactionDetailError(error));
}

export function* getTransactionDetailSaga() {
  yield takeLatest(TransacionesActionType.GET_TRANSACTION_DETAIL, getTransactionDetail);
}

function* getPaymentMethodsForTrans(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.allPaymentMethods}?include_deleted=1&page_size=50&country=${action.country}`,
    token: state.auth.token,
  });

  if (response) {
    yield put(getPaymentMethodsForTransSuccess(response));
  } else {
    yield put(getPaymentMethodsForTransError(error));
  }
}

export function* getPaymentMethodsForTransSaga() {
  yield takeLatest(TransacionesActionType.GET_PAYMENT_METHODS_FOR_TRANSACTIONS, getPaymentMethodsForTrans);
}

function* exportTransactions(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const params = action.filter ? `?${action.filter}` : '';
  const exportType: ExportType = action.exportType;

  const url = exportType === ExportType.XLSX ? Urls.api.exportTransactionsExcel : Urls.api.exportTransactions;

  const { response, error } = yield call(getTask, {
    url: url + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(exportTransactionsSuccess(response));
  } else {
    yield put(exportTransactionsError(error));
  }
}

export function* exportTransactionsSaga() {
  yield takeLatest(TransacionesActionType.EXPORT_TRANSACTIONS, exportTransactions);
}

function* getAffiliationsInfo() {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: Urls.api.affiliationsInfo,
    token: state.auth.token,
  });

  if (response) {
    yield put(getAffiliationsInfoSuccess(response));
  } else {
    yield put(getAffiliationsInfoError(error));
  }
}

export function* getAffiliationsInfoSaga() {
  yield takeLatest(TransacionesActionType.GET_AFFILIATIONS_INFO, getAffiliationsInfo);
}
